import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Checkbox } from "antd";
import CustomLayout from "../../../Core/Layout/CustomLayout";
import Button from "../../../Core/Components/CustomButton";
import { ChargerFillIcon } from "../../../Common/Svgs/ChargerIcon";
import CustomSwitch from "../../../Core/Components/CustomSwitch";
import SolarIcon from "../../../Common/Svgs/SolarIcon";
import CustomRange from "../../../Core/Components/CustomRange";
import PowerGridIcon from "../../../Common/Svgs/PowerGridIcon";
import capitalize from "../../../Utils/capitalize";

const Card = ({ title, current, setCurrent }) => {
  const getEnergySourceClass = (type) => {
    const base = "p-3.5 rounded-md cursor-not-allowed";

    if (current.switch) {
      if (current[type] > 0)
        return `${base} bg-yellow border border-transparent`;
      return `${base} bg-white border border-light-grey`;
    }

    return `${base} border border-light-grey`;
  };

  const handleEnergySource = (type) => {
    setCurrent((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  return (
    <div className="flex-1 bg-background p-4 rounded-md border border-light-grey cursor-not-allowed">
      <div className="flex items-center gap-x-2">
        <ChargerFillIcon size={20} className="text-yellow" />

        <p className="flex-1 font-poppins font-medium text-base">{title}</p>

        {/* should enable the solar source as well */}
        <CustomSwitch
          disabled
          checked={current.switch}
          onChange={(value) =>
            setCurrent((prev) => ({
              ...prev,
              switch: value,
              solar: value ? 100 : 0,
            }))
          }
        />
      </div>

      <div className="mt-[22px]">
        <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
          Energy Requirement
        </p>

        <div
          className={`flex items-center px-3.5 py-2.25 w-full rounded-md cursor-pointer border focus-within:border-green
            ${current.switch ? "bg-white" : "bg-transparent"}`}
        >
          <input
            disabled
            className="flex-1 bg-transparent border-none outline-none font-aileron text-xs font-semibold text-black placeholder:text-dark-gray"
            placeholder="Enter Value"
            onChange={(e) =>
              setCurrent((prev) => ({
                ...prev,
                energyRequirement: e.target.value,
              }))
            }
          />

          <p className="font-aileron text-10 font-normal text-dark-gray">kWh</p>
        </div>
      </div>

      {/* type of energy card */}
      <div className="mt-6 cursor-not-allowed">
        <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
          Energy Source
        </p>

        <div
          className={` ${getEnergySourceClass("solar")}`}
          onClick={() => handleEnergySource("solar")}
        >
          <div className="flex items-center gap-x-2">
            <Checkbox checked={current.solar} disabled />
            <p className="font-poppins text-xs font-medium">Solar</p>
          </div>

          <p className="ml-6 font-aileron text-xs font-normal text-dark-gray">
            15.93 kWh
          </p>
        </div>

        <div
          className={`mt-2 ${getEnergySourceClass("storage")}`}
          onClick={() => handleEnergySource("storage")}
        >
          <div className="flex items-center gap-x-2">
            <Checkbox checked={current.storage} disabled />
            <p className="font-poppins text-xs font-medium">Storage</p>
          </div>

          <p className="ml-6 font-aileron text-xs font-normal text-dark-gray">
            15.93 kWh
          </p>
        </div>

        <div
          className={`mt-2 ${getEnergySourceClass("grid")}`}
          onClick={() => handleEnergySource("grid")}
        >
          <div className="flex items-center gap-x-2">
            <Checkbox checked={current.grid} disabled />
            <p className="font-poppins text-xs font-medium">Grid</p>
          </div>

          <p className="ml-6 font-aileron text-xs font-normal text-dark-gray">
            Connected
          </p>
        </div>

        {/* ranges */}
        <div className="mt-6">
          <p className="font-aileron text-xs font-normal text-dark-gray capitalize mb-1.5">
            Energy Distribution
          </p>

          {/* solar text and percent */}
          <div className="flex items-center gap-x-2 w-full mb-2">
            <SolarIcon size={16} />
            <p className="flex-1 font-aileron text-xs font-normal">Solar</p>
            <p className="font-poppins text-xs font-medium">
              {current?.solar}%
            </p>
          </div>
          {/* solar range */}
          <div className="mt-1 pl-2">
            <CustomRange
              disabled={!current.switch}
              current={current.solar}
              onChange={(values) =>
                setCurrent((prev) => ({ ...prev, solar: values[0] }))
              }
              baseTrackColor="#DFDFDF"
              activeTrackColor="#FAD152"
              dotColor="#FAD152"
            />
          </div>

          {/* storage text and percent */}
          <div className="mt-3 flex items-center gap-x-2 w-full mb-2">
            <img
              src="/assets/icons/hubs/energy-storage.svg"
              className="w-4 h-4"
              alt="energy-storage"
            />

            <p className="flex-1 font-aileron text-xs font-normal">Storage</p>

            <p className="font-poppins text-xs font-medium">
              {current?.storage}%
            </p>
          </div>

          {/* storage range */}
          <div className="mt-1 pl-2">
            <CustomRange
              disabled={!current.switch}
              current={current.storage}
              onChange={(values) =>
                setCurrent((prev) => ({ ...prev, storage: values[0] }))
              }
              baseTrackColor="#DFDFDF"
              activeTrackColor="#0892BD"
              dotColor="#0892BD"
            />
          </div>

          {/* grid text and percent */}
          <div className="mt-3 flex items-center gap-x-2 w-full mb-2">
            <PowerGridIcon size={16} />
            <p className="flex-1 font-aileron text-xs font-normal">Grid</p>
            <p className="font-poppins text-xs font-medium">{current?.grid}%</p>
          </div>

          {/* grid range */}
          <div className="mt-1 pl-2">
            <CustomRange
              disabled={!current.switch}
              current={current.grid}
              onChange={(values) =>
                setCurrent((prev) => ({ ...prev, grid: values[0] }))
              }
              baseTrackColor="#DFDFDF"
              activeTrackColor="#085054"
              dotColor="#085054"
            />
          </div>
        </div>

        {/* start time and end time */}
        {/* <div className="mt-6 flex items-center justify-between">
          <div>
            <p className="mb-1.5 font-aileron text-xs font-normal text-dark-gray">
              Start Time
            </p>

            <TimeSelection
              current={current.startTime}
              disabled={!current.switch}
              onSelect={(item) =>
                setCurrent((prev) => ({ ...prev, startTime: item }))
              }
            />
          </div>

          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="30"
            height="56"
            fill="none"
            viewBox="0 0 30 56"
          >
            <path
              fill="#DFDFDF"
              d="M.333 38a2.667 2.667 0 1 0 5.334 0 2.667 2.667 0 0 0-5.334 0m24 0a2.667 2.667 0 1 0 5.334 0 2.667 2.667 0 0 0-5.334 0M3 38v.5h24v-1H3z"
            />
          </svg>

          <div>
            <p className="mb-1.5 font-aileron text-xs font-normal text-dark-gray">
              End Time
            </p>

            <TimeSelection
              current={current.endTime}
              disabled={!current.switch}
              onSelect={(item) =>
                setCurrent((prev) => ({ ...prev, endTime: item }))
              }
            />
          </div>
        </div> */}

        {/* <div className="mt-2 flex items-center justify-between">
          <p className="font-aileron text-xs font-normal text-dark-gray">
            Duration
          </p>

          <p className="font-poppins text-xs font-medium">
            {current.switch
              ? `${duration(current.startTime, current.endTime)}`
              : "0 Hrs"}
          </p>
        </div> */}
      </div>
    </div>
  );
};

const SupplyControlPanelScreen = () => {
  const navigate = useNavigate();

  const { selectedHub } = useSelector((state) => state.hubs);

  const [chargingStation, setChargingStation] = useState({
    switch: false,
    energyRequirement: 0,
    solar: 0,
    storage: 0,
    grid: 0,
    startTime: "06:00 AM",
    endTime: "06:00 PM",
  });

  const [coldStorage, setColdStorage] = useState({
    switch: false,
    energyRequirement: 0,
    solar: 0,
    storage: 0,
    grid: 0,
    startTime: "06:00 AM",
    endTime: "06:00 PM",
  });

  const [freezer, setFreezer] = useState({
    switch: false,
    energyRequirement: 0,
    solar: 0,
    storage: 0,
    grid: 0,
    startTime: "06:00 AM",
    endTime: "06:00 PM",
  });

  const [workingStation, setWorkingStation] = useState({
    switch: false,
    energyRequirement: 0,
    solar: 0,
    storage: 0,
    grid: 0,
    startTime: "06:00 AM",
    endTime: "06:00 PM",
  });

  return (
    <CustomLayout
      pageName={selectedHub?.name ? capitalize(selectedHub?.name) : ""}
      hideFooter
    >
      <div className="bg-background-white w-full h-full">
        <div className="flex items-center justify-between p-6 border-b border-light-grey">
          <p className="font-poppins text-base font-medium capitalize">
            supply control panel
          </p>

          <div className="flex items-center gap-x-3">
            <Button
              size="filter"
              width="generateUser"
              colors="grey"
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>

            <Button
              size="filter"
              width="generateUser"
              type="submit"
              onClick={() => navigate(-1)}
            >
              Save
            </Button>
          </div>
        </div>

        <div className="p-6 h-[calc(100vh-72px-89px)] overflow-y-scroll">
          <div className="flex items-center gap-x-4">
            <Card
              title="Charging Station"
              current={chargingStation}
              setCurrent={setChargingStation}
            />

            <Card
              title="Cold Storage"
              current={coldStorage}
              setCurrent={setColdStorage}
            />

            <Card title="Freezer" current={freezer} setCurrent={setFreezer} />

            <Card
              title="Working Station"
              current={workingStation}
              setCurrent={setWorkingStation}
            />
          </div>
        </div>
      </div>
    </CustomLayout>
  );
};

export default SupplyControlPanelScreen;
